<script lang="ts">
  // Overlays for the "harder" duel prompts that used to hang the engine: SELECT_SUM (sum-constrained
  // selection), SELECT_COUNTER (distribute counter removal), ANNOUNCE_RACE/ATTRIBUTE/CARD/NUMBER
  // (declare a value), and SORT_CARD/SORT_CHAIN (order cards). One bottom panel, branch per kind.
  import type { DuelChoice } from "@ygoevo/world-protocol";
  import type { Prompt } from "../../stores/duel.svelte.ts";
  import type { CardInfo } from "@ygoevo/yo2-data";
  import { fly, fade } from "svelte/transition";
  import { cardImage } from "../../../data.ts";
  import { searchCards } from "../../cards.ts";

  let { prompt, nameOf, onchoice }: { prompt: Prompt; nameOf: (c: number) => string; onchoice: (c: DuelChoice) => void } = $props();

  // bit → label tables (card attribute + race constants, common.h)
  const ATTRS: { bit: number; label: string }[] = [
    { bit: 0x1, label: "EARTH" }, { bit: 0x2, label: "WATER" }, { bit: 0x4, label: "FIRE" },
    { bit: 0x8, label: "WIND" }, { bit: 0x10, label: "LIGHT" }, { bit: 0x20, label: "DARK" }, { bit: 0x40, label: "DIVINE" },
  ];
  const RACES: { bit: bigint; label: string }[] = [
    [0x1, "Warrior"], [0x2, "Spellcaster"], [0x4, "Fairy"], [0x8, "Fiend"], [0x10, "Zombie"], [0x20, "Machine"],
    [0x40, "Aqua"], [0x80, "Pyro"], [0x100, "Rock"], [0x200, "Winged Beast"], [0x400, "Plant"], [0x800, "Insect"],
    [0x1000, "Thunder"], [0x2000, "Dragon"], [0x4000, "Beast"], [0x8000, "Beast-Warrior"], [0x10000, "Dinosaur"],
    [0x20000, "Fish"], [0x40000, "Sea Serpent"], [0x80000, "Reptile"], [0x100000, "Psychic"], [0x200000, "Divine-Beast"],
    [0x400000, "Creator God"], [0x800000, "Wyrm"], [0x1000000, "Cyberse"], [0x2000000, "Illusion"],
  ].map(([b, label]) => ({ bit: BigInt(b as number), label: label as string }));

  // ── per-kind state, reset on a new prompt ──
  let picked = $state<number[]>([]);      // selectSum: indices into `cards`
  let counts = $state<number[]>([]);      // selectCounter: per-card removal
  let attribs = $state<number>(0);        // announceAttrib bitmask
  let racemask = $state<bigint>(0n);      // announceRace bitmask
  let order = $state<number[]>([]);       // sort: card indices in chosen order
  let query = $state("");
  let results = $state<CardInfo[]>([]);
  $effect(() => { void prompt; picked = []; counts = []; attribs = 0; racemask = 0n; order = []; query = ""; results = []; });

  // ── SELECT_SUM ──
  const sumCards = $derived(prompt.kind === "selectSum" ? prompt.cards : []);
  const sumMust = $derived(prompt.kind === "selectSum" ? prompt.must : []);
  const sumTarget = $derived(prompt.kind === "selectSum" ? prompt.sumVal : 0);
  const sumv1 = (param: number) => param & 0xffff;
  const sumv2 = (param: number) => (param >> 16) & 0xffff;
  /** A dual-value card (Tuningware class: counts as either of two levels) shows both on its badge. */
  const paramLabel = (p: number) => (sumv2(p) ? `${sumv1(p)}/${sumv2(p)}` : `${sumv1(p)}`);
  const sumTotal = $derived(
    sumMust.reduce((a, c) => a + sumv1(c.param), 0) + picked.reduce((a, i) => a + sumv1(sumCards[i]?.param ?? 0), 0),
  );
  // Mirror of the core's acceptance (playerop.cpp SelectSum) — NOT plain equality. Mode 0 (bounded
  // count): SOME o1/o2 assignment across must+picked must hit the target EXACTLY. Mode 1 ("at
  // least", rituals): reachable with the max contributions AND minimal — dropping the smallest pick
  // would fall below the target. Gating Confirm on Σlow16 === target left dual-level materials and
  // overshoot rituals (only Level-4s for a Level-7 ritual) permanently unconfirmable.
  const coreExact = (params: number[], index: number, acc: number): boolean => {
    if (acc <= 0 || index >= params.length) return false;
    const o1 = sumv1(params[index]!), o2 = sumv2(params[index]!);
    if (index === params.length - 1) return acc === o1 || acc === o2;
    return (acc > o1 && coreExact(params, index + 1, acc - o1)) || (o2 > 0 && acc > o2 && coreExact(params, index + 1, acc - o2));
  };
  const sumOk = $derived.by(() => {
    if (prompt.kind !== "selectSum") return false;
    const params = [...sumMust.map((c) => c.param), ...picked.map((i) => sumCards[i]?.param ?? 0)];
    if (prompt.mode === 0) {
      if (picked.length < (prompt.min || 0) || picked.length > (prompt.max || Infinity)) return false;
      return coreExact(params, 0, sumTarget);
    }
    let total = 0, mx = 0, mn = Infinity;
    for (const p of params) {
      const o1 = sumv1(p), o2 = sumv2(p);
      const ms = o2 && o2 < o1 ? o2 : o1;
      total += ms; mx += Math.max(o1, o2);
      if (ms < mn) mn = ms;
    }
    return mx >= sumTarget && total - mn < sumTarget;
  });
  const toggleSum = (i: number) => {
    if (picked.includes(i)) { picked = picked.filter((x) => x !== i); return; }
    if (prompt.kind === "selectSum" && prompt.mode === 0 && picked.length >= (prompt.max || Infinity)) return; // bounded: the core rejects count > max
    picked = [...picked, i];
  };

  // ── SELECT_COUNTER ──
  const cntCards = $derived(prompt.kind === "selectCounter" ? prompt.cards : []);
  const cntTarget = $derived(prompt.kind === "selectCounter" ? prompt.count : 0);
  const cntTotal = $derived(counts.reduce((a, b) => a + (b || 0), 0));
  const bump = (i: number, d: number) => {
    const max = cntCards[i]?.counters ?? 0;
    // Build a full-length array FIRST — counts starts empty, so mapping over it would drop the very
    // first increment (it had no slot to write into).
    const next = cntCards.map((_, j) => counts[j] || 0);
    next[i] = Math.max(0, Math.min(max, (counts[i] || 0) + d));
    counts = next;
  };

  // ── ANNOUNCE_NUMBER ──
  const numbers = $derived(prompt.kind === "announceNumber" ? prompt.numbers : []);

  // ── ANNOUNCE_RACE / ATTRIB ──
  const annCount = $derived(prompt.kind === "announceRace" || prompt.kind === "announceAttrib" ? prompt.count : 1);
  const availAttr = $derived(prompt.kind === "announceAttrib" ? prompt.available : 0);
  const availRace = $derived(prompt.kind === "announceRace" ? BigInt(prompt.available) : 0n);
  const attrCount = $derived(ATTRS.reduce((n, a) => n + (attribs & a.bit ? 1 : 0), 0));
  const raceCount = $derived(RACES.reduce((n, a) => n + ((racemask & a.bit) !== 0n ? 1 : 0), 0));
  function toggleAttr(bit: number) { attribs = attribs & bit ? attribs & ~bit : (attrCount < annCount ? attribs | bit : attribs); }
  function toggleRace(bit: bigint) {
    const has = (racemask & bit) !== 0n;
    racemask = has ? racemask & ~bit : (Number(raceCount) < annCount ? racemask | bit : racemask);
  }

  // ── ANNOUNCE_CARD search (debounced) ──
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  $effect(() => {
    const q = query.trim();
    void prompt;
    clearTimeout(searchTimer);
    if (prompt.kind !== "announceCard" || q.length < 2) { results = []; return; }
    searchTimer = setTimeout(async () => {
      try { results = (await searchCards({ q, limit: 24 })).cards; } catch { results = []; }
    }, 220);
  });

  // ── SORT ──
  const sortCards = $derived(prompt.kind === "sortCard" || prompt.kind === "sortChain" ? prompt.cards : []);
  const clickSort = (i: number) => { if (!order.includes(i)) order = [...order, i]; };
  const sortDone = $derived(order.length === sortCards.length && sortCards.length > 0);

  const heading = $derived.by(() => {
    switch (prompt.kind) {
      case "selectSum": return "Select cards";
      case "selectCounter": return "Remove counters";
      case "announceRace": return "Declare a Type";
      case "announceAttrib": return "Declare an Attribute";
      case "announceCard": return "Declare a card";
      case "announceNumber": return "Declare a number";
      case "sortCard": return "Arrange the cards";
      case "sortChain": return "Order the chain";
      default: return "Select";
    }
  });
</script>

<div class="pointer-events-none fixed bottom-0 left-0 right-[var(--rail)] z-[55] flex justify-center px-3 pb-[calc(var(--mhand)+0.4rem)] xl:left-[var(--info)]">
  <div class="pointer-events-auto flex max-w-[min(94vw,880px)] flex-col gap-2.5 chamfer bg-void/93 px-4 py-3 ring-1 ring-cyan/35 backdrop-blur-md" style="--c:10px;box-shadow:0 10px 40px rgba(0,0,0,.55)" in:fly={{ y: 24, duration: 200 }} out:fade={{ duration: 120 }}>
    <div class="flex items-center justify-between gap-3">
      <span class="font-display text-xs font-bold uppercase tracking-[0.16em] text-cyan" style="text-shadow:0 0 12px var(--color-cyan)">{heading}</span>
      {#if prompt.kind === "selectSum"}
        <span class="font-display text-[0.66rem] uppercase tracking-wide tabular-nums {sumOk ? 'text-lime' : 'text-ink-dim/80'}">{sumTotal} / {sumTarget}</span>
      {:else if prompt.kind === "selectCounter"}
        <span class="font-display text-[0.66rem] uppercase tracking-wide tabular-nums {cntTotal === cntTarget ? 'text-lime' : 'text-ink-dim/80'}">{cntTotal} / {cntTarget}</span>
      {:else if prompt.kind === "announceRace" || prompt.kind === "announceAttrib"}
        <span class="font-display text-[0.66rem] uppercase tracking-wide tabular-nums text-ink-dim/80">{prompt.kind === "announceAttrib" ? attrCount : Number(raceCount)} / {annCount}</span>
      {/if}
    </div>

    {#if prompt.kind === "selectSum"}
      <!-- must-select (locked) + selectable, gated on the running param sum -->
      <div class="flex flex-wrap justify-center gap-2">
        {#each sumMust as c (c.sequence + ":" + c.location)}
          <div class="w-[54px] shrink-0 overflow-hidden chamfer opacity-90 ring-1 ring-gold/60" style="--c:5px" title={nameOf(c.code)}>
            <img src={cardImage(c.code)} alt="" class="aspect-[59/86] w-full object-cover" draggable="false" />
            <span class="block bg-gold/20 py-px text-center font-display text-[0.5rem] font-bold text-gold">{paramLabel(c.param)}</span>
          </div>
        {/each}
        {#each sumCards as c, i (i)}
          {@const sel = picked.includes(i)}
          <button class="w-[54px] shrink-0 overflow-hidden chamfer ring-1 transition-all hover:scale-105 {sel ? 'ring-lime' : 'ring-cyan/40'}" style="--c:5px;{sel ? 'box-shadow:0 0 12px color-mix(in oklab,var(--color-lime) 55%,transparent)' : ''}" title={nameOf(c.code)} onclick={() => toggleSum(i)}>
            <img src={cardImage(c.code)} alt="" class="aspect-[59/86] w-full object-cover {sel ? '' : 'opacity-85'}" draggable="false" />
            <span class="block py-px text-center font-display text-[0.5rem] font-bold {sel ? 'bg-lime/25 text-lime' : 'text-ink-dim/70'}">{paramLabel(c.param)}</span>
          </button>
        {/each}
      </div>
      <div class="flex justify-end">
        <button class="chamfer-btn border px-4 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider transition-colors {sumOk ? 'border-lime/60 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:4px" disabled={!sumOk} onclick={() => onchoice({ kind: "selectCards", indices: picked })}>Confirm</button>
      </div>

    {:else if prompt.kind === "selectCounter"}
      <div class="flex flex-wrap justify-center gap-2.5">
        {#each cntCards as c, i (i)}
          <div class="flex w-[64px] flex-col items-center gap-1">
            <div class="relative w-full overflow-hidden chamfer ring-1 ring-cyan/40" style="--c:5px" title={nameOf(c.code)}>
              <img src={cardImage(c.code)} alt="" class="aspect-[59/86] w-full object-cover" draggable="false" />
              <span class="absolute right-0.5 top-0.5 grid size-4 place-items-center rounded-full bg-void/85 font-display text-[0.5rem] font-bold text-gold">{c.counters}</span>
            </div>
            <div class="flex items-center gap-1">
              <button class="grid size-5 place-items-center chamfer-btn border border-cyan/45 font-display text-xs text-cyan" style="--c:3px" onclick={() => bump(i, -1)}>–</button>
              <span class="w-4 text-center font-display text-xs font-bold tabular-nums text-ink">{counts[i] || 0}</span>
              <button class="grid size-5 place-items-center chamfer-btn border border-cyan/45 font-display text-xs text-cyan" style="--c:3px" onclick={() => bump(i, 1)}>+</button>
            </div>
          </div>
        {/each}
      </div>
      <div class="flex justify-end">
        <button class="chamfer-btn border px-4 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider transition-colors {cntTotal === cntTarget ? 'border-lime/60 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:4px" disabled={cntTotal !== cntTarget} onclick={() => onchoice({ kind: "counter", counts: cntCards.map((_, i) => counts[i] || 0) })}>Confirm</button>
      </div>

    {:else if prompt.kind === "announceAttrib"}
      <div class="flex flex-wrap justify-center gap-1.5">
        {#each ATTRS.filter((a) => availAttr & a.bit) as a (a.bit)}
          {@const on = (attribs & a.bit) !== 0}
          <button class="chamfer-btn border px-3 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider transition-colors {on ? 'border-lime/60 bg-lime/12 text-lime' : 'border-cyan/40 text-cyan-dim hover:text-cyan'}" style="--c:4px" onclick={() => toggleAttr(a.bit)}>{a.label}</button>
        {/each}
      </div>
      <div class="flex justify-end"><button class="chamfer-btn border px-4 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider {attrCount === annCount ? 'border-lime/60 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:4px" disabled={attrCount !== annCount} onclick={() => onchoice({ kind: "attrib", value: attribs })}>Confirm</button></div>

    {:else if prompt.kind === "announceRace"}
      <div class="flex max-h-[34dvh] flex-wrap justify-center gap-1.5 overflow-y-auto scroll-cyber">
        {#each RACES.filter((a) => (availRace & a.bit) !== 0n) as a (a.bit)}
          {@const on = (racemask & a.bit) !== 0n}
          <button class="chamfer-btn border px-2.5 py-1.5 font-display text-[0.6rem] font-bold uppercase tracking-wide transition-colors {on ? 'border-lime/60 bg-lime/12 text-lime' : 'border-cyan/40 text-cyan-dim hover:text-cyan'}" style="--c:4px" onclick={() => toggleRace(a.bit)}>{a.label}</button>
        {/each}
      </div>
      <div class="flex justify-end"><button class="chamfer-btn border px-4 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider {Number(raceCount) === annCount ? 'border-lime/60 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:4px" disabled={Number(raceCount) !== annCount} onclick={() => onchoice({ kind: "race", value: racemask.toString() })}>Confirm</button></div>

    {:else if prompt.kind === "announceNumber"}
      <div class="flex flex-wrap justify-center gap-1.5">
        {#each numbers as n, i (i)}
          <button class="chamfer-btn border border-gold/45 bg-void/60 px-4 py-2 font-display text-sm font-bold tabular-nums text-gold transition-colors hover:bg-gold/15" style="--c:4px" onclick={() => onchoice({ kind: "announceNumber", index: i })}>{n}</button>
        {/each}
      </div>

    {:else if prompt.kind === "announceCard"}
      <input
        bind:value={query}
        placeholder="Type a card name…"
        onkeydown={(e) => e.stopPropagation()}
        class="w-full chamfer bg-void-2/70 px-3 py-2 font-display text-sm text-ink ring-1 ring-cyan/35 outline-none placeholder:text-ink-dim/50"
        style="--c:5px"
      />
      <div class="flex max-h-[36dvh] flex-wrap justify-center gap-2 overflow-y-auto scroll-cyber">
        {#each results as c (c.id)}
          <button class="w-[54px] shrink-0 overflow-hidden chamfer ring-1 ring-cyan/40 transition-all hover:ring-cyan hover:scale-105" style="--c:5px" title={c.name} onclick={() => onchoice({ kind: "announceCard", code: c.id })}>
            <img src={cardImage(c.id)} alt={c.name} class="aspect-[59/86] w-full object-cover" draggable="false" />
          </button>
        {/each}
        {#if query.trim().length >= 2 && results.length === 0}<span class="self-center font-display text-[0.62rem] uppercase tracking-wide text-ink-dim/50">No matches</span>{/if}
      </div>

    {:else if prompt.kind === "sortCard" || prompt.kind === "sortChain"}
      <div class="flex flex-wrap justify-center gap-2">
        {#each sortCards as c, i (i)}
          {@const pos = order.indexOf(i)}
          <button class="relative w-[54px] shrink-0 overflow-hidden chamfer ring-1 transition-all hover:scale-105 {pos >= 0 ? 'ring-lime' : 'ring-cyan/40'}" style="--c:5px" title={nameOf(c.code)} onclick={() => clickSort(i)}>
            <img src={cardImage(c.code)} alt="" class="aspect-[59/86] w-full object-cover {pos >= 0 ? '' : 'opacity-80'}" draggable="false" />
            {#if pos >= 0}<span class="absolute right-0.5 top-0.5 grid size-4 place-items-center rounded-full bg-lime font-display text-[0.55rem] font-bold text-void">{pos + 1}</span>{/if}
          </button>
        {/each}
      </div>
      <div class="flex justify-end gap-1.5">
        <button class="chamfer-btn border border-ink-dim/40 bg-void/60 px-3 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider text-ink-dim transition-colors hover:text-ink" style="--c:4px" onclick={() => onchoice({ kind: "sort", order: null })}>Auto</button>
        <button class="chamfer-btn border px-4 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider {sortDone ? 'border-lime/60 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:4px" disabled={!sortDone} onclick={() => onchoice({ kind: "sort", order })}>Confirm</button>
      </div>
    {/if}
  </div>
</div>
